import { useEffect, useRef, useState } from "react";
import { RiUpload2Line, RiSaveLine, RiBookmark2Line } from "react-icons/ri";
import Button from "../../Utility/Button/Button";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import AddButton from "../../Utility/AddButton/AddButton";
import ToggleSwitch from "../../Utility/ToggleSwitch/ToggleSwitch";

export default function IssueListToolbar({
  exportDropdownRef,
  exportDropdownOpen,
  setExportDropdownOpen,
  onCreateClick,
  showProjectExport = false,
  exportLoading = false,
  onExportCsv,
  onExportXlsx,
  /** When false, hides the create button (e.g. global Kanban has its own create). */
  showCreate = true,
  /** When false, hides Save As — shown on {@link SavedSearchBar} for global Issues instead. */
  showSaveAs = true,
  // ── Save filter props ──
  isDirty = false,
  activeSavedSearchId = null,
  saving = false,
  onSave,
  onSaveAs,
  showEpicsToggle = false,
  showEpicsOn = false,
  onShowEpicsChange,
  hideDoneToggle = false,
  hideDoneOn = false,
  onHideDoneChange,
}) {
  const [saveAsMode, setSaveAsMode] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const saveAsInputRef = useRef(null);

  useEffect(() => {
    if (saveAsMode && saveAsInputRef.current) {
      saveAsInputRef.current.focus();
    }
  }, [saveAsMode]);

  const handleSaveAsSubmit = async (e) => {
    e.preventDefault();
    if (!saveAsName.trim()) return;
    await onSaveAs?.(saveAsName.trim());
    setSaveAsName("");
    setSaveAsMode(false);
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
      {(showEpicsToggle && typeof onShowEpicsChange === "function") ||
      (hideDoneToggle && typeof onHideDoneChange === "function") ? (
        <div className="flex items-center gap-4 mr-auto">
          {showEpicsToggle && typeof onShowEpicsChange === "function" ? (
            <div className="flex items-center gap-2">
              <Typography variant="text2" className="text-text-primary whitespace-nowrap m-0 p-0">
                Show epics
              </Typography>
              <ToggleSwitch
                checked={!!showEpicsOn}
                onChange={onShowEpicsChange}
                size="sm m-0 p-0"
                aria-label="Show epics in work item list"
              />
            </div>
          ) : null}

          {hideDoneToggle && typeof onHideDoneChange === "function" ? (
            <div className="flex items-center gap-2">
              <Typography variant="text2" className="text-text-primary whitespace-nowrap m-0 p-0">
                Hide done
              </Typography>
              <ToggleSwitch
                checked={!!hideDoneOn}
                onChange={onHideDoneChange}
                size="sm m-0 p-0"
                aria-label="Hide done work items"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {showProjectExport && (
        <div className="relative" ref={exportDropdownRef}>
          <Button
            variant="neutral"
            size="vsm"
            title="Export"
            className="btn-reset square-btn"
            data-tooltipp="Export"
            disabled={exportLoading}
            onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
          >
            <RiUpload2Line className="text-lg text-text-secondary" />
          </Button>

          {exportDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <Button
                type="button"
                disabled={exportLoading}
                onClick={() => onExportCsv?.()}
                className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
              >
                <Typography variant="text2" component="span">
                  Export CSV
                </Typography>
              </Button>
              <Button
                type="button"
                disabled={exportLoading}
                onClick={() => onExportXlsx?.()}
                className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
              >
                <Typography variant="text2" component="span">
                  Export XLSX
                </Typography>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Save filter controls — shown when filters are modified */}
      {isDirty && (
        <>
          {activeSavedSearchId && (
            <Button
              type="button"
              onClick={onSave}
              disabled={saving}
              variant="secondary"
              size="vsm"
              className="!inline-flex !items-center !gap-1.5 !rounded-full !px-3 !py-1 !text-xs !font-medium !text-blue-600 !bg-blue-50 !border !border-blue-200 hover:!bg-blue-100 !normal-case"
            >
              <RiSaveLine size={12} />
              {saving ? "Saving…" : "Save filter"}
            </Button>
          )}

          {showSaveAs &&
            (saveAsMode ? (
              <form onSubmit={handleSaveAsSubmit} className="inline-flex items-center gap-1.5">
                <Input
                  ref={saveAsInputRef}
                  name="saveAsName"
                  type="text"
                  value={saveAsName}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  placeholder="Enter search name…"
                  maxLength={100}
                  noWrapper={true}
                  className="!h-[30px] !min-h-0 !w-44 !rounded-lg !px-2.5 !py-0 !text-xs"
                />
                <Button
                  type="submit"
                  disabled={saving || !saveAsName.trim()}
                  variant="primary"
                  size="vsm"
                  className="!h-[30px] !min-h-[30px] !rounded-lg !px-2.5 !py-0 !text-xs"
                >
                  {saving ? "…" : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="neutral"
                  size="vsm"
                  onClick={() => {
                    setSaveAsMode(false);
                    setSaveAsName("");
                  }}
                  className="!h-[30px] !min-h-[30px] !rounded-lg !px-2.5 !py-0 !text-xs"
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <Button
                type="button"
                variant="neutral"
                size="vsm"
                onClick={() => setSaveAsMode(true)}
                className="!inline-flex !h-[30px] !min-h-[30px] !items-center !gap-1.5 !rounded-lg !border !border-border-primary !bg-surface-primary !px-3 !py-0 !text-xs !font-medium !normal-case hover:!bg-[var(--bg-secondary)]"
              >
                <RiBookmark2Line size={12} className="shrink-0" />
                Save As…
              </Button>
            ))}
        </>
      )}

      {showCreate ? <AddButton onClick={onCreateClick} label="Create new work item" /> : null}
    </div>
  );
}
